import React from "react";
import ModalForm from "./Form";
import type { ModalProps } from ".";
import Modal from ".";
import type { Variant } from "../Button";

type ConfirmProps = {
  body: React.ReactNode;
  okButtonName?: string;
  okButtonVariant?: Variant;
} & Pick<ModalProps, "subtitle" | "title">;

type ModalState = {
  active?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
} & ConfirmProps;

const ConfirmModalContext = React.createContext<(configObject: ConfirmProps) => Promise<boolean>>(
  () => Promise.resolve(true),
);

interface P {
  children: React.ReactNode;
}

const ConfirmModalProvider = ({ children, ...props }: P) => {
  const [modal, setModal] = React.useState<ModalState>();

  const confirm = React.useCallback(
    ({ title, body, subtitle, okButtonName, okButtonVariant }: ConfirmProps) =>
      new Promise<boolean>(resolve =>
        setModal({
          active: true,
          title,
          subtitle,
          body,
          okButtonName,
          okButtonVariant,
          onSubmit: () => resolve(true),
          onCancel: () => resolve(false),
        }),
      ),
    [],
  );

  const handleClose = React.useCallback(() => {
    if (!modal) return;
    modal.onCancel();
    setModal({ ...modal, active: false });
  }, [modal]);

  return (
    <ConfirmModalContext.Provider value={confirm} {...props}>
      {children}
      {modal && (
        <Modal
          title={modal.title}
          isOpen={modal.active || false}
          subtitle={modal.subtitle}
          onRequestClose={handleClose}
        >
          <ModalForm
            onRequestClose={handleClose}
            onSubmit={() => {
              modal.onSubmit();
              return true;
            }}
            focusOk
            okButtonName={modal.okButtonName}
            okButtonVariant={modal.okButtonVariant}
          >
            {modal.body}
          </ModalForm>
        </Modal>
      )}
    </ConfirmModalContext.Provider>
  );
};

export default ConfirmModalProvider;

export { ConfirmModalContext };
